import { useState, useMemo } from "react"
import type { GuidebookData } from "../types"
import "./concierge.css"
import { getVoiceCopy } from "../../house-voice-copy"
import { adaptToConciergeData } from "./adapter"
import { ConciergeHero } from "./ConciergeHero"
import { BottomNav, type NavTab } from "./BottomNav"
import { EssentialsSection } from "./EssentialsSection"
import { PlacesSection } from "./PlacesSection"
import { StayTimeline } from "./StayTimeline"
import { HouseRulesSection } from "./HouseRulesSection"
import { PinUnlockModal } from "./PinUnlockModal"
import { SectionLabel } from "./SectionLabel"

interface Props {
  data: GuidebookData
  slug?: string
  guestToken?: string
  onRefresh?: () => void
  showUnlockPrompt?: boolean
  onOpenUnlock?: () => void
}

export function ConciergeShell({ data, slug, guestToken, onRefresh, showUnlockPrompt, onOpenUnlock }: Props) {
  const [activeTab, setActiveTab] = useState<NavTab>("home")
  const [pinOpen, setPinOpen] = useState(false)

  const cd = adaptToConciergeData(data)
  const voiceCopy = useMemo(() => getVoiceCopy(data.house_voice), [data.house_voice])
  const isIcalLocked = cd.accessState === "ical-locked"

  function handleUnlock() {
    if (isIcalLocked && guestToken) {
      setPinOpen(true)
    } else if (onOpenUnlock) {
      onOpenUnlock()
    }
  }

  function handlePinSuccess() {
    setPinOpen(false)
    onRefresh?.()
  }

  return (
    <div className="gb-concierge">
      {activeTab === "home" && (
        <>
          <ConciergeHero
            propertyName={cd.propertyName}
            address={cd.address}
            heroMessage={cd.heroMessage}
          />
          <div className="gc-tab-panel">
            <div className="gc-section">
              <SectionLabel text="Stay essentials" />
              <EssentialsSection
                wifiName={cd.wifiName}
                wifiPassword={cd.wifiPassword}
                doorCode={cd.doorCode}
                doorCodeVisibility={cd.doorCodeVisibility}
                doorCodePolicyText={cd.doorCodePolicyText}
                accessState={cd.accessState}
                onUnlock={(showUnlockPrompt || isIcalLocked) ? handleUnlock : undefined}
              />
            </div>

            {(cd.checkInDate || cd.checkOutDate || cd.checkinTime) && (
              <div className="gc-section">
                <SectionLabel text="Your stay" />
                <StayTimeline
                  checkinTime={cd.checkinTime}
                  checkoutTime={cd.checkoutTime}
                  checkInDate={cd.checkInDate}
                  checkOutDate={cd.checkOutDate}
                />
              </div>
            )}

            {cd.welcome?.message && (
              <div className="gc-section">
                <SectionLabel text="Welcome" />
                <div className="gc-card" style={{ padding: "20px 18px" }}>
                  {cd.welcome.headline && (
                    <div style={{
                      fontFamily: "Cormorant Garamond, serif",
                      fontSize: "1.15rem",
                      fontWeight: 300,
                      marginBottom: 10,
                      color: "var(--gc-text)",
                    }}>
                      {cd.welcome.headline}
                    </div>
                  )}
                  <div style={{ fontSize: 14, lineHeight: 1.65, color: "var(--gc-text-muted)" }}>
                    {cd.welcome.message}
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === "explore" && (
        <div className="gc-tab-panel">
          <div className="gc-section" style={{ paddingTop: 32 }}>
            <SectionLabel text="Discover nearby" />
            <PlacesSection places={cd.places} highlights={cd.highlights} badgeLabel={voiceCopy.gb_places_badge} />
          </div>
        </div>
      )}

      {activeTab === "guide" && (
        <div className="gc-tab-panel">
          {data.property && (
            <div className="gc-section" style={{ paddingTop: 32 }}>
              <SectionLabel text="About the area" />
              {data.property.region_description && (
                <div className="gc-card" style={{ padding: "18px", marginBottom: 16 }}>
                  <p style={{ margin: 0, fontSize: 14, lineHeight: 1.65, color: "var(--gc-text-muted)" }}>
                    {data.property.region_description}
                  </p>
                </div>
              )}
            </div>
          )}

          {Object.keys(cd.houseRules || {}).length > 0 && (
            <div className="gc-section">
              <SectionLabel text="House rules" />
              <HouseRulesSection houseRules={cd.houseRules} />
            </div>
          )}

          {(data.property.parking_instruction || data.property.trash_instruction) && (
            <div className="gc-section">
              <SectionLabel text="Property info" />
              <div className="gc-card gc-card--divided">
                {data.property.parking_instruction && (
                  <div className="gc-rule-row">
                    <div className="gc-rule-icon">🅿️</div>
                    <div>
                      <div className="gc-rule-label">Parking</div>
                      <div className="gc-rule-detail">{data.property.parking_instruction}</div>
                    </div>
                  </div>
                )}
                {data.property.trash_instruction && (
                  <div className="gc-rule-row">
                    <div className="gc-rule-icon">🗑️</div>
                    <div>
                      <div className="gc-rule-label">Trash & recycling</div>
                      <div className="gc-rule-detail">{data.property.trash_instruction}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <BottomNav active={activeTab} onChange={setActiveTab} />

      {isIcalLocked && slug && guestToken && (
        <PinUnlockModal
          open={pinOpen}
          slug={slug}
          guestToken={guestToken}
          onClose={() => setPinOpen(false)}
          onSuccess={handlePinSuccess}
        />
      )}
    </div>
  )
}
